import { useState, useCallback, useMemo, useRef } from "react"

import { MinorOccupation, MinorOccupationsMap } from "~/api/student/profile/_get-types/JobHunting"
import type MasterDataStore from "~/stores/MasterDataStore"
import { reduceToObject } from "~/utils"

import type { FormSelectboxForOccupationsProps } from "./index"

type Dict = Record<number, boolean | undefined>

const dictToArray = (dict: Dict): Array<string> => {
  const tupleArray = Object.entries(dict)
  const ids = tupleArray.filter(([, isSelected]) => isSelected).map(([id]) => id)
  return ids
}

const arrayToDict = (ids: Array<string | number>): Dict => {
  return reduceToObject(ids, (id) => [id, true])
}

export function convertOccupationToText(majorOccupationText: string, minorOccupationTexts: Array<string>): string {
  return `${majorOccupationText} > ${minorOccupationTexts.join(", ") || "未選択"}`
}

/**
 * minorOccupation (興味のある職種/選択肢小項目)の型を変換する
 * @param minorOccupations MinorOccupation[]: profileStore.json.job_hunting.interest.minor_occupations
 * @returns MinorOccupationsMap: {大項目のid: [大項目に連なる小項目]}
 */
export function convertMinorOccupationsToMap(minorOccupations: MinorOccupation[]): MinorOccupationsMap {
  return minorOccupations.reduce((obj, minorOccupation) => {
    const current = obj[minorOccupation.major_occupation_id] ?? []
    obj[minorOccupation.major_occupation_id] = [...current, minorOccupation]
    return obj
  }, {} as MinorOccupationsMap)
}

/**
 * minorOccupation を majorOccupation の key で再構成する
 * @return type: {[major_occupation_id: number]: MinorOccupation}[]
 */
export const generateOccupations = (masterDataStore: MasterDataStore) => {
  const { majorOccupations, minorOccupations } = masterDataStore
  if (!majorOccupations || !minorOccupations) {
    return
  }
  /** majorOccupationId ごとに分けられた Dict */
  const minorOccupationsMap = convertMinorOccupationsToMap(minorOccupations as MinorOccupation[])

  return majorOccupations.map((majorOccupation) => ({
    ...majorOccupation,
    minor_occupations: minorOccupationsMap[majorOccupation.id] ?? [],
  }))
}

export const useFormSelectboxForOccupations = (props: FormSelectboxForOccupationsProps) => {
  const {
    occupations,
    onChangeForMajorOccupationIds,
    onChangeForMinorOccupationIds,
    selectedForMajorOccupationIds,
    selectedForMinorOccupationIds,
  } = props

  const [selectedMajorOccupationDict, setSelectedMajorOccupationDict] = useState<Dict>(() =>
    arrayToDict(selectedForMajorOccupationIds)
  )
  const [selectedMajorOccupationIds, setSelectedMajorOccupationIds] = useState(selectedForMajorOccupationIds)

  const [selectedMinorOccupationDict, setSelectedMinorOccupationDict] = useState<Dict>(() =>
    arrayToDict(selectedForMinorOccupationIds)
  )

  const [isShownModal, setIsShownModal] = useState(false)

  const beforeIdsRef = useRef({
    majorOccupationIds: undefined! as FormSelectboxForOccupationsProps["selectedForMajorOccupationIds"],
    minorOccupationIds: undefined! as FormSelectboxForOccupationsProps["selectedForMinorOccupationIds"],
  })

  const onInputClick = useCallback(() => {
    setIsShownModal(true)
    // モーダルを開いたときの状態を保持
    beforeIdsRef.current = {
      majorOccupationIds: selectedForMajorOccupationIds,
      minorOccupationIds: selectedForMinorOccupationIds,
    }
  }, [selectedForMajorOccupationIds, selectedForMinorOccupationIds])

  const onClickForMajorOccupations = useCallback((clickedId: number, beforeValue: boolean | undefined) => {
    setSelectedMajorOccupationDict((dict) => ({ ...dict, [clickedId]: !beforeValue }))

    const toggled = !beforeValue
    // NOTE: クリックした majorOccupation がfalseなら minorOccupation を全てfalseにする
    if (!toggled) {
      const minorOccupations = occupations.find(({ id }) => id === clickedId)!.minor_occupations
      const nextDict = reduceToObject(minorOccupations, ({ id }) => [id, false])
      setSelectedMinorOccupationDict((dict) => ({ ...dict, ...nextDict }))
    }
  }, [])

  const onClickForMinorOccupations = useCallback((id: number) => {
    setSelectedMinorOccupationDict((dict) => ({ ...dict, [id]: !dict[id] }))
  }, [])

  const onCompleteClick = useCallback(() => {
    setIsShownModal(false)

    const majorOccupationIds = dictToArray(selectedMajorOccupationDict)
    setSelectedMajorOccupationIds(majorOccupationIds)
    onChangeForMajorOccupationIds?.(majorOccupationIds)

    const minorOccupationIds = dictToArray(selectedMinorOccupationDict)
    onChangeForMinorOccupationIds?.(minorOccupationIds)
  }, [selectedMajorOccupationDict, selectedMinorOccupationDict])

  const onCancelClick = useCallback(() => {
    setIsShownModal(false)
    const { majorOccupationIds, minorOccupationIds } = beforeIdsRef.current
    setSelectedMajorOccupationIds(majorOccupationIds)
    setSelectedMajorOccupationDict(() => reduceToObject(majorOccupationIds, (id) => [id, true]))
    setSelectedMinorOccupationDict(() => reduceToObject(minorOccupationIds, (id) => [id, true]))
  }, [])

  const textForInputLike = useMemo(() => {
    if (selectedMajorOccupationIds.length === 0) {
      return "選択してください"
    }
    const text = selectedMajorOccupationIds
      .map((majorOccupationId) => {
        const occupation = occupations.find(({ id }) => id == majorOccupationId)!
        const majorOccupationText = occupation.name
        const minorOccupationTexts = occupation.minor_occupations
          .filter(({ id }) => selectedMinorOccupationDict[id])
          .map(({ name }) => name)

        return convertOccupationToText(majorOccupationText, minorOccupationTexts)
      })
      .join(" / ")

    return text
  }, [selectedMajorOccupationIds, selectedMinorOccupationDict])

  return {
    selectedMajorOccupationDict,
    onClickForMajorOccupations,
    selectedMinorOccupationDict,
    onClickForMinorOccupations,
    textForInputLike,
    isShownModal,
    onInputClick,
    onCompleteClick,
    onCancelClick,
  }
}
